import { useEffect, useRef } from "react";
import { useSession, type DeviceInfo } from "../app/session";

async function post(path: string): Promise<void> {
  await fetch(path, { method: "POST", credentials: "same-origin" });
}

/** *Lend this device* (P4): the owner's session ends and the picker shows for someone else. */
export async function lendDevice(): Promise<void> {
  await post("/api/internal/device/lend");
  location.assign("/");
}

/** *Give back*: the lent session ends; the device opens as its owner again. */
export async function giveBack(): Promise<void> {
  await post("/api/internal/device/give-back");
  location.assign("/");
}

/** What the top bar's profile button does on this device: switch, lend or give back. */
export function deviceAction(
  device: DeviceInfo | null | undefined,
  profileId: string | undefined,
): "switch" | "lend" | "giveBack" {
  if (device?.lent) return "giveBack";
  if (device?.mode === "personal" && device.ownerId === profileId) return "lend";
  return "switch";
}

/**
 * Back to the picker after some minutes without use (P4): a lent session after 15 minutes, a shared device after
 * the minutes set for it. While the app is used it tells the server at most once a minute, so the server's own
 * limit (for a closed or sleeping app) counts from the last use too.
 */
export function useIdleReturn() {
  const device = useSession((s) => s.session?.device);
  const signedIn = useSession((s) => Boolean(s.session?.principal));
  const lastUse = useRef(0);
  const lastPing = useRef(0);
  const limit = !signedIn
    ? null
    : device?.lent
      ? device.lendIdleMinutes
      : device?.mode === "shared"
        ? device.idleMinutes
        : null;
  useEffect(() => {
    if (!limit) return;
    lastUse.current = Date.now();
    const used = () => {
      const now = Date.now();
      lastUse.current = now;
      if (now - lastPing.current > 60_000) {
        lastPing.current = now;
        void post("/api/internal/device/ping");
      }
    };
    const check = () => {
      if (Date.now() - lastUse.current < limit * 60_000) return;
      if (device?.lent) void giveBack();
      else
        void fetch("/api/internal/logout", { method: "POST", credentials: "same-origin" }).then(() =>
          location.assign("/"),
        );
    };
    const events = ["pointerdown", "keydown", "wheel", "touchstart"] as const;
    for (const e of events) window.addEventListener(e, used, { passive: true });
    document.addEventListener("visibilitychange", check);
    const timer = window.setInterval(check, 15_000);
    return () => {
      for (const e of events) window.removeEventListener(e, used);
      document.removeEventListener("visibilitychange", check);
      window.clearInterval(timer);
    };
  }, [limit, device?.lent]);
}
